/**
 * Chip — compact labeled pill. Used for Member positions, units, and the
 * AI/Human kind marker on MemberCard.
 *
 * Variants name *role*, not color:
 *   - neutral: structural facts (a position, a unit, a count)
 *   - ai:      identifies an AI Member; labeled "AI" — never color-only
 *   - status-*: success/warning/danger surfaces (reserved for future use)
 *
 * Optional `onRemove` renders a close button; the chip itself remains a
 * span (not a button) so consumer pages can wrap it in an <a> or omit
 * interactivity. The close button gets its own accessible name.
 */

import { forwardRef, type HTMLAttributes, type MouseEventHandler } from 'react';
import styles from './Chip.module.css';

type ChipVariant = 'neutral' | 'ai' | 'status-success' | 'status-warning' | 'status-danger';
type ChipSize = 'sm' | 'md';

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: ChipVariant;
  size?: ChipSize;
  onRemove?: MouseEventHandler<HTMLButtonElement>;
  removeLabel?: string;
}

const VARIANT_CLASS: Record<ChipVariant, string | undefined> = {
  neutral: styles.variantNeutral,
  ai: styles.variantAi,
  'status-success': styles.variantStatusSuccess,
  'status-warning': styles.variantStatusWarning,
  'status-danger': styles.variantStatusDanger,
};

const SIZE_CLASS: Record<ChipSize, string | undefined> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
};

export const Chip = forwardRef<HTMLSpanElement, ChipProps>(function Chip(
  { variant = 'neutral', size = 'sm', onRemove, removeLabel, className, children, ...rest },
  ref,
) {
  const composedClassName = [styles.chip, VARIANT_CLASS[variant], SIZE_CLASS[size], className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <span ref={ref} className={composedClassName} {...rest}>
      <span className={styles.label}>{children}</span>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel ?? 'Remove'}
          className={styles.remove}
        >
          <span aria-hidden="true">×</span>
        </button>
      ) : null}
    </span>
  );
});
